import { useState, useEffect } from "react";

// Third Party
import { useQuery } from "@tanstack/react-query";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Formik, Field } from "formik";

// UI
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import TableSkeleton from "@/components/skeleton/TableSkeleton";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import FormattedNumberInput from "@/components/FormattedNumberInput";

// Icons
import { Pen, Save } from "lucide-react";

// Third Party
import type { MutateLandAsset } from "@/types/soe";

// Services
import { createLandAsset, updateLandAsset } from "@/services/soe";
import { getLandFundTypes } from "@/services/master";

export const Route = createFileRoute(
	"/_layout-private/account-management/soe/_information/land/mutate",
)({
	component: MutateLand,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			id: search.id as string | undefined,
			data: search.data as string | undefined,
		};
	},
});

function MutateLand() {
	const id = String(localStorage.getItem("companyId")).replace(/['"]+/g, "");
	const navigate = useNavigate();
	const { id: landId, data } = Route.useSearch();
	const { toast } = useToast();
	const queryClient = useQueryClient();

	const [parseData, setParseData] = useState({} as MutateLandAsset);

	// Query
	const { data: landFundTypesRes, isLoading } = useQuery({
		queryKey: ["landFundTypes"],
		queryFn: () => getLandFundTypes(),
	});

	// Mutations
	const createLandAssetMutation = useMutation({
		mutationFn: (data: MutateLandAsset) => createLandAsset(data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["lands", id],
			});
			toast({
				title: "ສຳເລັດ",
				description: "ສ້າງຂໍ້ມູນທີ່ດິນສຳເລັດ",
			});
			navigate({
				to: "..",
				replace: true,
			});
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	const updateLandAssetMutation = useMutation({
		mutationFn: (data: MutateLandAsset) => updateLandAsset(landId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["lands", id],
			});
			toast({
				title: "ສຳເລັດ",
				description: "ແກ້ໄຂຂໍ້ມູນທີ່ດິນສຳເລັດ",
			});
			navigate({
				to: "..",
				replace: true,
			});
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	// Effects

	useEffect(() => {
		if (data) {
			console.log(JSON.parse(data), "Data");
			setParseData(JSON.parse(data));
		}
	}, [data]);

	// Functions

	const handleOnSubmit = (values: MutateLandAsset) => {
		landId
			? updateLandAssetMutation.mutate({
					...values,
					landStatusId: parseData?.landStatusId,
				})
			: createLandAssetMutation.mutate(values);
	};

	// Data
	const landFundTypes = landFundTypesRes?.data as {
		id: string;
		name: string;
	}[];

	// Generate years from 1975 to current year
	const generateYears = () => {
		const currentYear = new Date().getFullYear();
		const years = [];
		for (let year = currentYear; year >= 1975; year--) {
			years.push(year);
		}
		return years;
	};

	const years = generateYears();

	const mutationPending =
		createLandAssetMutation.isPending || updateLandAssetMutation.isPending;

	if (isLoading) {
		return <TableSkeleton />;
	}

	return (
		<div className="space-y-[24px]">
			<div className="text-[18px]">
				<h2 className="text-zinc-950">ຂ້ໍມູນທີ່ດິນ</h2>
				<p className="text-zinc-500">
					ສິ່ງທີ່ທ່ານປ້ອນໄປຈະຖືກນຳໄປບັນທຶກເປັນຂໍ້ມູນຄະນະອຳນວຍການຂອງລັດວິສາຫະກິດ
				</p>

				<Separator className="my-6" />
			</div>

			<form
				onSubmit={(e) => {
					e.preventDefault();
					const formData = new FormData(e.target as HTMLFormElement);

					// Debug: Log all form data
					console.log("Form data entries:");
					for (const [key, value] of formData.entries()) {
						console.log(key, value);
					}

					const payload = {
						companyId: id,
						yearly: Number(formData.get("yearly")) || new Date().getFullYear(),
						title: formData.get("title") as string,
						landType: formData.get("landType") as string,
						landFundTypeId: formData.get("landFundTypeId") as string,
						initialValue: Number(formData.get("initialValue")) || 0,
						totalArea: formData.get("totalArea") as string,
						location: formData.get("location") as string,
						currentValue: Number(formData.get("currentValue")) || 0,
						usage: formData.get("usage") as string,
						remark: formData.get("remark") as string,
					};

					console.log("Payload:", payload); // Debug log
					handleOnSubmit(payload as MutateLandAsset);
				}}
				className="space-y-[16px]"
			>
				<div>
					<Label htmlFor="yearly">
						ປີ <span className="text-red-500">*</span>
					</Label>
					<Select
						name="yearly"
						defaultValue={
							parseData.yearly?.toString() ||
							new Date().getFullYear().toString()
						}
						required
						disabled={!!parseData.companyId}
					>
						<SelectTrigger>
							<SelectValue placeholder="ເລືອກປີ..." />
						</SelectTrigger>
						<SelectContent>
							{years.map((year) => (
								<SelectItem value={year.toString()} key={year}>
									{year}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div>
					<Label htmlFor="title">
						ເນື້ອໃນທີ່ດິນ <span className="text-red-500">*</span>
					</Label>
					<Input
						required
						name="title"
						defaultValue={parseData.title || ""}
						placeholder="ເນື້ອໃນທີ່ດິນ..."
					/>
				</div>
				<div>
					<Label htmlFor="landType">
						ປະເພດທີ່ດິນ <span className="text-red-500">*</span>
					</Label>
					<Input
						required
						name="landType"
						defaultValue={parseData.landType || ""}
						placeholder="ປະເພດທີ່ດິນ..."
					/>
				</div>
				<div>
					<Label htmlFor="totalArea">
						ເນື້ອທີ່ທັງໝົດ (ມ2/ຮຕ) <span className="text-red-500">*</span>
					</Label>
					<Input
						required
						name="totalArea"
						defaultValue={parseData.totalArea || ""}
						placeholder="ເນື້ອທີ່ທັງໝົດ..."
					/>
				</div>
				<div>
					<Label htmlFor="location">
						ທີ່ຕັ້ງຂອງດິນ <span className="text-red-500">*</span>
					</Label>
					<Input
						required
						name="location"
						defaultValue={parseData.location || ""}
						placeholder="ທີ່ຕັ້ງຂອງດິນ..."
					/>
				</div>
				<div>
					<Label htmlFor="landFundTypeId">ທຶນໃນການຈັດຊື້</Label>
					<Select
						name="landFundTypeId"
						defaultValue={parseData.landFundTypeId || ""}
						required
					>
						<SelectTrigger>
							<SelectValue placeholder="ເລືອກທຶນໃນການຈັດຊື້" />
						</SelectTrigger>
						<SelectContent>
							{landFundTypes?.map((item) => (
								<SelectItem value={item.id} key={item.id}>
									{item?.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div>
					<FormattedNumberInput
						id="initialValue"
						name="initialValue"
						label="ມູນຄ່າເບື້ອງຕົ້ນ"
						defaultValue={parseData.initialValue}
						required={true}
						placeholder="ລັດປະກອບເປັນທຶນ..."
					/>
				</div>
				<div>
					<FormattedNumberInput
						id="currentValue"
						name="currentValue"
						label="ມູນຄ່າໃນປັດຈຸບັນ"
						defaultValue={parseData.currentValue}
						required={true}
						placeholder="ມູນຄ່າປັດຈຸບັນ..."
					/>
				</div>
				<div>
					<Label htmlFor="usage">
						ການນຳໃຊ້ທີ່ດິນ <span className="text-red-500">*</span>
					</Label>
					<Input
						required
						name="usage"
						defaultValue={parseData.usage || ""}
						placeholder="ການນຳໃຊ້ທີ່ດິນ..."
					/>
				</div>
				<div>
					<Label htmlFor="remark">ໝາຍເຫດ</Label>
					<Textarea
						name="remark"
						defaultValue={parseData.remark || ""}
						placeholder="ໝາຍເຫດ..."
					/>
				</div>

				<div className="flex items-center justify-end w-full gap-4">
					<Button
						disabled={mutationPending}
						type="button"
						variant={"outline"}
						onClick={() =>
							navigate({
								to: "..",
								replace: true,
							})
						}
					>
						ຍົກເລີກ
					</Button>
					<Button type="submit" disabled={mutationPending}>
						{mutationPending ? (
							<Spinner className="text-white" />
						) : landId ? (
							<>
								<Pen /> ແກ້ໄຂ
							</>
						) : (
							<>
								<Save /> ບັນທຶກ
							</>
						)}
					</Button>
				</div>
			</form>
		</div>
	);
}
